import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import SEO, { collectionSchema } from "@/components/SEO";

export default function CompaniesListPage() {
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/companies"), api.get("/projects")])
      .then(([c, p]) => {
        if (!mounted) return;
        if (Array.isArray(c.data)) setCompanies(c.data);
        if (Array.isArray(p.data)) setProjects(p.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const projectCountByCompany = projects.reduce((acc, p) => {
    if (p.company) acc[p.company] = (acc[p.company] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="companies-list-page">
      <SEO
        title="Companies"
        description="Public and private operators, developers, and juniors active in Canadian mining — with tickers, headquarters, and links to their active project pipelines."
        jsonLd={collectionSchema("Companies", "Canadian mining companies directory", "/companies")}
      />
      <section className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <div className="bb-overline text-bb-copper">Company Directory</div>
          <h1
            className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
            data-testid="companies-page-headline"
          >
            Companies
          </h1>
          <p className="mt-5 text-base lg:text-lg text-bb-slate max-w-2xl leading-relaxed">
            Public and private operators, developers, and juniors active in
            Canadian mining — with tickers, headquarters, and links to their
            active project pipeline.
          </p>
          <div className="mt-6 bb-overline text-bb-slate">
            {companies.length} companies
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="bb-container">
          {loading ? (
            <div className="text-center py-20 bb-overline text-bb-slate">
              Loading directory…
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="companies-grid"
            >
              {companies.map((c) => (
                <Link
                  key={c.id}
                  to={`/companies/${c.slug}`}
                  className="bb-card p-6 flex flex-col gap-3 group"
                  data-testid={`company-card-${c.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                      <Building2 size={18} strokeWidth={1.8} />
                    </span>
                    <span className="font-mono text-[11px] text-bb-slate">
                      {c.ticker} · {c.exchange}
                    </span>
                  </div>
                  <h3 className="font-archivo text-xl text-bb-navy font-semibold tracking-tight leading-tight mt-1">
                    <span className="bb-link">{c.name}</span>
                  </h3>
                  <p className="text-sm text-bb-slate leading-relaxed line-clamp-3">
                    {c.description}
                  </p>
                  <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {(c.commodities || []).slice(0, 3).map((cm) => (
                        <span
                          key={cm}
                          className="bb-tag-neutral text-[10px] font-mono px-2 py-0.5"
                        >
                          {cm}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-mono text-bb-slate whitespace-nowrap">
                        {projectCountByCompany[c.id] || 0} project
                        {(projectCountByCompany[c.id] || 0) === 1 ? "" : "s"}
                      </span>
                      <ArrowUpRight
                        size={14}
                        className="text-bb-navy group-hover:text-bb-copper transition-colors"
                      />
                    </div>
                  </div>
                  <div className="text-[11px] font-mono text-bb-slate inline-flex items-center gap-1.5">
                    <MapPin size={11} /> {c.headquarters}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
